import React, { useRef, useEffect, useLayoutEffect, useState } from "react";
import { Helmet } from "react-helmet";
import "../../../App.css";
import featuredVideo from "../../../assets/Baticon.mp4";
import titleImage from "../../../assets/TitleMSB.png";

const Home = () => {
  const pageRef = useRef(null);
  const videoRef = useRef(null);
  const [commandCount, setCommandCount] = useState(0);
  const [serverCount, setServerCount] = useState(0);
  const TOTAL_COMMANDS = 56;

  useEffect(() => {
    let cmd = 0;
    let srv = 0;
    let targetServers = 0;
    let interval;

    fetch("/api/servers", {
      credentials: "include",
      cache: "no-store",
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        targetServers = Number(data.totalServers || 0);

        interval = setInterval(() => {
          let changed = false;

          if (cmd < TOTAL_COMMANDS) {
            cmd++;
            setCommandCount(cmd);
            changed = true;
          }

          if (srv < targetServers) {
            srv++;
            setServerCount(srv);
            changed = true;
          }

          if (!changed) {
            clearInterval(interval);
          }
        }, 20);
      })
      .catch((err) => {
        console.error("❌ Failed to fetch server count:", err);

        interval = setInterval(() => {
          if (cmd < TOTAL_COMMANDS) {
            cmd++;
            setCommandCount(cmd);
          } else {
            clearInterval(interval);
          }
        }, 20);
      });

    return () => {
      if (interval) clearInterval(interval);
    };
  }, []);

  useLayoutEffect(() => {
    const previousScrollRestoration = window.history.scrollRestoration;
    const root = document.documentElement;
    const previousInlineScrollBehavior = root.style.scrollBehavior;

    window.history.scrollRestoration = "manual";
    root.style.scrollBehavior = "auto";

    const forceTop = () => {
      window.scrollTo(0, 0);
      root.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    forceTop();

    const frameId = window.requestAnimationFrame(forceTop);
    const topLockTimer = window.setTimeout(() => {
      forceTop();
      root.style.scrollBehavior = previousInlineScrollBehavior;
    }, 80);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(topLockTimer);
      root.style.scrollBehavior = previousInlineScrollBehavior;
      window.history.scrollRestoration = previousScrollRestoration;
    };
  }, []);

  useEffect(() => {
    const page = pageRef.current;

    if (!page) return undefined;

    const introParagraph = page.querySelector(".msb-intro-paragraph");
    const orderedRevealElements = Array.from(
      page.querySelectorAll(
        ".msb-scroll-header, .msb-scroll-paragraph:not(.msb-intro-paragraph), .msb-stagger-list"
      )
    );

    let introReady = false;
    let userHasScrolledDown = false;
    let nextRevealIndex = 0;
    let lastScrollY = Math.max(window.scrollY || window.pageYOffset || 0, 0);
    let revealPassMode = null;
    let revealTimer = null;
    let introFollowupTimer = null;
    let destroyed = false;

    const REVEAL_STEP_DELAY = 260;
    const INTRO_FOLLOWUP_DELAY = 520;

    const revealElement = (element) => {
      if (element.classList.contains("msb-stagger-list")) {
        const items = Array.from(
          element.querySelectorAll(".msb-list-item")
        );

        items.forEach((item, index) => {
          item.style.transitionDelay = `${index * 135}ms`;
          item.classList.add("is-visible");
        });

        return;
      }

      element.classList.add("is-visible");
    };

    const getViewportHeight = () =>
      window.innerHeight || document.documentElement.clientHeight || 0;

    const getScrollY = () =>
      Math.max(window.scrollY || window.pageYOffset || 0, 0);

    const isActuallyInViewport = (element) => {
      const rect = element.getBoundingClientRect();
      const viewportHeight = getViewportHeight();

      return (
        rect.top <= viewportHeight * 0.92 &&
        rect.bottom >= viewportHeight * 0.04
      );
    };

    const hasBeenReached = (element) => {
      const rect = element.getBoundingClientRect();
      const viewportHeight = getViewportHeight();

      // Once the top of the next narrative block reaches this lower viewport
      // zone, it is eligible. If the user scrolls past it quickly, rect.top is
      // even smaller, so the catch-up pass still reveals it instead of
      // permanently blocking every later element in the sequence.
      return rect.top <= viewportHeight * 0.86;
    };

    const isAtDocumentBottom = () => {
      const viewportHeight = getViewportHeight();
      const scrollY = getScrollY();
      const documentHeight = Math.max(
        document.documentElement.scrollHeight,
        document.body ? document.body.scrollHeight : 0
      );

      return scrollY + viewportHeight >= documentHeight - 24;
    };

    const canRevealNext = (element, mode) => {
      if (mode === "startup") {
        return isActuallyInViewport(element);
      }

      return hasBeenReached(element) || isAtDocumentBottom();
    };

    const continueRevealPass = () => {
      revealTimer = null;

      if (destroyed || !introReady) return;

      if (nextRevealIndex >= orderedRevealElements.length) {
        revealPassMode = null;
        return;
      }

      const nextElement = orderedRevealElements[nextRevealIndex];
      const mode = revealPassMode || "scroll";

      if (!canRevealNext(nextElement, mode)) {
        revealPassMode = null;
        return;
      }

      revealElement(nextElement);
      nextRevealIndex += 1;

      revealTimer = window.setTimeout(
        continueRevealPass,
        REVEAL_STEP_DELAY
      );
    };

    const requestRevealPass = (mode) => {
      if (!introReady || destroyed) return;

      // A real downward scroll always takes priority over the startup viewport
      // fill because it must be allowed to catch up anything already reached.
      if (mode === "scroll") {
        revealPassMode = "scroll";
      } else if (!revealPassMode) {
        revealPassMode = "startup";
      }

      if (!revealTimer) {
        continueRevealPass();
      }
    };

    const handleScroll = () => {
      const currentScrollY = getScrollY();
      const movedDown = currentScrollY > lastScrollY + 1;

      if (movedDown && currentScrollY > 2) {
        userHasScrolledDown = true;

        if (introReady) {
          requestRevealPass("scroll");
        }
      }

      lastScrollY = currentScrollY;
    };

    const handleResize = () => {
      if (!introReady) return;

      requestRevealPass(userHasScrolledDown ? "scroll" : "startup");
    };

    // The intro remains the guaranteed first narrative animation. Nothing
    // else is allowed to reveal during initial mounting. After the intro has
    // landed, the controller fills only the narrative blocks that genuinely
    // fit inside the current viewport. Smaller screens therefore show only
    // the intro, while taller screens do not look artificially unfinished.
    const introTimer = window.setTimeout(() => {
      if (introParagraph) {
        introParagraph.classList.add("is-visible");
      }

      introReady = true;
      lastScrollY = getScrollY();

      introFollowupTimer = window.setTimeout(() => {
        if (destroyed) return;

        requestRevealPass(
          userHasScrolledDown ? "scroll" : "startup"
        );
      }, INTRO_FOLLOWUP_DELAY);
    }, 650);

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleResize);

    return () => {
      destroyed = true;
      window.clearTimeout(introTimer);

      if (introFollowupTimer) {
        window.clearTimeout(introFollowupTimer);
      }

      if (revealTimer) {
        window.clearTimeout(revealTimer);
      }

      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleMouseEnter = () => {
    if (videoRef.current) {
      videoRef.current.play().catch((error) => {
        console.error("Video playback failed:", error);
      });
    }
  };

  const handleMouseLeave = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  return (
    <div ref={pageRef} className="home-container msb-page">
      <Helmet>
        <title>Magical Sorting Bat - Discord Bot</title>
      </Helmet>

      <h1 className="hidden-title">MSB - Discord Bot</h1>

      <img
        src={titleImage}
        alt="Magical Sorting Bat"
        className="featured-image"
      />

      <div className="video-stats-wrapper">
        <div className="stat-box left-box">
          <h3>Commands</h3>
          <span>{commandCount}</span>
        </div>

        <div
          className="hover-container"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <video
            ref={videoRef}
            className="featured-video"
            muted
            loop
            playsInline
            preload="metadata"
          >
            <source src={featuredVideo} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>

        <div className="stat-box right-box">
          <h3>Servers</h3>
          <span>{serverCount}</span>
        </div>
      </div>

      <p className="msb-scroll-paragraph msb-intro-paragraph">
        The Discord Bot that needs no introduction, though he's gonna serve you
        one regardless. Armed with gadgets and inspired by Bob Kane. 
        <b> Magical Sorting Bat (MSB)</b> watches over the world of Pogforts and it's allies. Kickin'
        some serious ass with his own brand of justice and execution. Now
        available to add in your own server. Magical Sorting Bat is the swiss
        army knife of Discord bots. The bot that does it all in the dark of
        night because he hates every light theme. The Bat always remains
        operational with 99.9% uptime, and will continue to expand further
        alongside his active developers, devoted students, and citizens of
        Pogforts.
      </p>
      <h3 className="msb-scroll-header">...a bat with a purpose...</h3>
      <p className="msb-scroll-paragraph">
        There's no two ways about it. The Magical Sorting Bat is necessary in the realms of Discord.
        In spaces dominated by generic low value bots, warewolves, and the criminally insane. <b>The Bat Strikes Back. </b>
        Every bat has its breaking point, and this bat has had enough of anime girls, turtles, prehistoric fossils, and those silly blue smurfs.
      </p>
      <h3 className="msb-scroll-header">...he spins...he cleans...<br></br>...and he's just plain mean...</h3>
      <p className="msb-scroll-paragraph">
        Supporting friends and terrifying enemies all in a days work.
        It would be a crime to overlook his dedication to serving the realms.
        Sometimes, we can do so much it's hard to list everything without sounding a complete ego maniac.
        So prepare for a little flash bang of what this badman can do.
      </p>
      <h3 className="msb-scroll-header">...right off the bat...</h3>
      <ul className="msb-stagger-list">
        <li className="msb-list-item">Create professional servers</li>
        <li className="msb-list-item">Increase server engagement</li>
        <li className="msb-list-item">Moderate server channels</li>
        <li className="msb-list-item">Stronger security posture</li>
        <li className="msb-list-item">Track statistics & invites</li>
        <li className="msb-list-item">Make customised embeds</li>
        <li className="msb-list-item">Get tarot card readings</li>
        <li className="msb-list-item">Automate dungeons & dragons</li>
        <li className="msb-list-item">Funny social interactions</li>
        <li className="msb-list-item">Become a Pogforts partner</li>
        <li className="msb-list-item">Relax with 99.9% uptime</li>
        <li className="msb-list-item">Look damn cool doing it</li>
      </ul>
      <h3 className="msb-scroll-header">...be that one guy...</h3>
      <p className="msb-scroll-paragraph">
        By helping a hero, you become the hero yourself, <b>"It's not who you are behind the screen,
        but what you do that defines who you are."</b> Help the bat in setting the new standard, in a
        world where nothing changes, except the price of your ignorance.
      </p>

      <div className="home-action-row">
        <a
          className="home-action-button"
          href={
            window.location.hostname === "localhost" ||
            window.location.hostname === "127.0.0.1"
              ? "http://localhost:5000/api/auth/discord/bot/install"
              : "/api/auth/discord/bot/install"
          }
        >
          ADD BOT
        </a>
      </div>
    </div>
  );
};

export default Home;
